/**
 * PostsGrid (Phase 2.5) — replaces `PostsTabInline` on the Posts tab of
 * `<TopicDetailPanel>`. Phase 2 ships single-mode only; multi-mode + sprint
 * overlay land in Phase 3 + Phase 4.
 *
 * Layout (CSS grid):
 *   col 1   = aspect label (row header)
 *   col 2   = master cell
 *   col 3-N = derivative cells per topic.targetPlatforms
 *
 * Rows = topic.postBreakdown[] aspects; or a single virtual row labeled
 * with topic.angle when plannedPostCount === 1.
 *
 * Click on empty cell → opens `<PerCellGenerateDialog>` with cellMode='single'
 * pre-selected. Submission hits `/api/agent/content-plan/generate-posts` with
 * `cellScope` + `applyMode='auto_apply_topic'` so the resulting ContentPost
 * docs stream into the grid via the same `useTopicPostsGrid` subscription.
 */

import React, { useMemo, useState } from 'react';
import {
  Box, Stack, Typography, Alert, Chip, CircularProgress, IconButton,
  Menu, MenuItem, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import { useTopicPostsGrid, NO_ASPECT_KEY } from '../../hooks/useTopicPostsGrid';
import { MasterCell } from './MasterCell';
import { DerivativeCell } from './DerivativeCell';
import { PerCellGenerateDialog } from './PerCellGenerateDialog';
import { SDialog } from '@modules/ui-components';
import { AvailableModelSelector } from '@modules/ai-settings';
import { ContentPlanApiClient } from '../../services/ContentPlanApiClient';
import { contentPostGridService } from '../../services/ContentPostGridService';
import { contentPostFirestoreService } from '../../services/ContentPostFirestoreService';
import { useProjectDefaultModel } from '../../hooks/useProjectDefaultModel';
import { useAuthor } from '../../hooks/useAuthor';
import { useAuthorPlatforms } from '../../hooks/useAuthorPlatforms';
import { TopicBacklogFirestoreService } from '../../services/TopicBacklogFirestoreService';
import { UnifiedPostDialog } from '../postLike/UnifiedPostDialog';
import {
  contentPostToPostLike,
  postLikeToContentPostPatch,
  type PostLike,
} from '../../utils/postLike';
import type { ContentTopic, PlatformId, Post, PublicationSlotAssignment } from '../../types';

export interface PostsGridProps {
  projectId: string;
  userId: string;
  authorId: string;
  topic: ContentTopic;
  /** When set, generation submits as `applyMode='auto_apply_sprint'` and stamps sprintId. */
  strategyId?: string;
  sprintId?: string;
  publicationId?: string;
  publicationPlatform?: string;
  publicationAspectKey?: string | null;
  publicationSlotAssignment?: PublicationSlotAssignment | null;
}

interface AspectRow {
  /** Stored value used for grid keying + Firestore queries. NO_ASPECT_KEY for single-aspect topics. */
  key: string;
  /** Persisted aspectKey to send in cellScope (null for single-aspect). */
  scopeAspectKey: string | null;
  /** Display label. */
  label: string;
  /** Orphan rows surface posts whose aspectKey is not in the current
   *  `topic.postBreakdown[]` — typically posts created before aspects were
   *  added, or posts whose aspect was later removed. Generate wand is hidden
   *  on these rows to avoid producing more orphans. */
  isOrphan?: boolean;
}

export const PostsGrid: React.FC<PostsGridProps> = ({
  projectId, userId, authorId, topic, strategyId, sprintId,
  publicationId, publicationPlatform, publicationAspectKey, publicationSlotAssignment,
}) => {
  const grid = useTopicPostsGrid(projectId, topic.id, authorId);
  const projectDefaultModel = useProjectDefaultModel(projectId);
  // Author's enabled platforms are used as the fallback chain when
  // topic.targetPlatforms is empty AND for the
  // "+ Platform" picker (filters out platforms already added).
  const { author } = useAuthor(projectId);
  const { platforms: authorPlatforms } = useAuthorPlatforms(projectId, authorId || author?.id || null);
  const activeAuthorPlatformIds = useMemo(
    () => authorPlatforms
      .filter((p) => p.isActive !== false)
      .map((p) => p.id as PlatformId),
    [authorPlatforms],
  );
  const [busyCell, setBusyCell] = useState<string | null>(null);
  /**
   * Phase 3 follow-up — pending generation tasks per cell. Keyed by cellKey
   * (`${aspect}::master` or `${aspect}::${platform}`). Set when the per-cell
   * dialog returns success with a taskId (the dialog closes immediately,
   * `busyCell` resets, but Gemini is still running for 5-15s). Each cell
   * subscribes via `<CellTaskBadge taskId>` — the badge reads the global
   * background-tasks Zustand store and auto-hides when the task hits a
   * terminal status. Map entries are pruned when the resulting post-doc
   * appears in the grid (the cell stops being "empty").
   */
  const [pendingTasks, setPendingTasks] = useState<Map<string, string>>(
    () => new Map(),
  );

  // Prune pendingTasks when the corresponding cell becomes non-empty —
  // i.e. the post-doc resulting from the generation has been written to
  // Firestore and picked up by `useTopicPostsGrid`. After this, the cell
  // renders the post itself with its own `lastTaskId` linkage; the pending
  // entry is no longer needed.
  React.useEffect(() => {
    if (pendingTasks.size === 0) return;
    const stillPending = new Map<string, string>();
    for (const [cellKey, taskId] of pendingTasks.entries()) {
      const [aspect, suffix] = cellKey.split('::');
      const aspectK = aspect === NO_ASPECT_KEY ? NO_ASPECT_KEY : aspect;
      let cellHasData = false;
      if (suffix === 'master') {
        cellHasData = grid.mastersByAspect.has(aspectK);
      } else {
        const aspectCells = grid.cells.get(aspectK);
        const platformCell = aspectCells?.get(suffix as PlatformId);
        cellHasData = (platformCell?.items?.length ?? 0) > 0;
      }
      if (!cellHasData) {
        stillPending.set(cellKey, taskId);
      }
    }
    if (stillPending.size !== pendingTasks.size) {
      setPendingTasks(stillPending);
    }
  }, [grid.mastersByAspect, grid.cells, pendingTasks]);
  /** Phase 3.5 — row-level "Regenerate stale" batch in flight. Keys = aspect row keys. */
  const [staleRegenBusy, setStaleRegenBusy] = useState<Set<string>>(new Set());
  const [staleRegenDialog, setStaleRegenDialog] = useState<{
    rowKey: string;
    aspectKey: string | null;
    count: number;
  } | null>(null);
  const [staleRegenModel, setStaleRegenModel] = useState(projectDefaultModel.value);
  const [batchError, setBatchError] = useState<string | null>(null);
  const [activeDialog, setActiveDialog] = useState<{
    aspectKey: string | null;
    platform: PlatformId;
    target: 'master' | 'derivative';
  } | null>(null);
  const [addPlatformAnchor, setAddPlatformAnchor] = useState<HTMLElement | null>(null);
  // Phase 3.6 — single post-detail dialog instance shared across cells.
  // Click on any post body opens here; UnifiedPostDialog handles edit + save
  // via contentPostFirestoreService.updateContent (canonical content_posts path).
  const [openPost, setOpenPost] = useState<Post | null>(null);

  const matchingPublicationIdForCell = (platform: PlatformId, aspectKey: string | null): string | undefined => {
    if (!publicationId) return undefined;
    if (publicationPlatform && publicationPlatform !== platform) return undefined;
    if ((publicationAspectKey ?? null) !== aspectKey) return undefined;
    return publicationId;
  };
  const isSlotFocusMode = Boolean(publicationId);
  const isSlotCell = (platform: PlatformId, aspectKey: string | null): boolean =>
    Boolean(matchingPublicationIdForCell(platform, aspectKey));

  React.useEffect(() => {
    if (staleRegenDialog) setStaleRegenModel(projectDefaultModel.value);
  }, [staleRegenDialog, projectDefaultModel.value]);

  // ── Rows ──────────────────────────────────────────────────────
  // Two-tier model:
  //   1. Configured rows from `topic.postBreakdown[]` (or a single null-row
  //      fallback when no breakdown / single-aspect topic).
  //   2. Orphan rows for any aspectKey appearing in persisted posts but NOT
  //      in postBreakdown. Covers legacy posts (created before aspects were
  //      added) + posts whose aspectKey was removed later. These are
  //      read-only — wand is disabled to prevent further orphan growth.
  const aspectRows: AspectRow[] = useMemo(() => {
    const breakdown = ((topic as any).postBreakdown ?? []) as Array<{
      aspectKey: string;
      aspectLabel: string;
    }>;
    const planned = ((topic as any).plannedPostCount ?? 1) as number;

    const configured: AspectRow[] = (planned <= 1 || breakdown.length === 0)
      ? [{
          key: NO_ASPECT_KEY,
          scopeAspectKey: null,
          label: topic.angle ?? '(topic)',
        }]
      : breakdown.map((b) => ({
          key: b.aspectKey,
          scopeAspectKey: b.aspectKey,
          label: b.aspectLabel || b.aspectKey,
        }));

    // Orphan detection — keys from persisted data minus configured keys.
    const configuredKeys = new Set(configured.map((r) => r.key));
    const dataKeys = new Set<string>([
      ...grid.mastersByAspect.keys(),
      ...grid.cells.keys(),
    ]);
    const orphanRows: AspectRow[] = [];
    for (const k of dataKeys) {
      if (configuredKeys.has(k)) continue;
      orphanRows.push({
        key: k,
        scopeAspectKey: k === NO_ASPECT_KEY ? null : k,
        label: k === NO_ASPECT_KEY
          ? '(no aspect — unscoped posts)'
          : `Unmapped aspect: ${k}`,
        isOrphan: true,
      });
    }
    return [...configured, ...orphanRows];
  }, [topic, grid.mastersByAspect, grid.cells]);

  // ── Columns (Phase 3.5.3 precedence chain) ────────────────────
  // 1. topic.targetPlatforms — the canonical per-topic override.
  // 2. active author platforms — fallback when targetPlatforms is empty.
  // 3. empty array → render an Alert "Configure author + platforms first".
  const platforms: PlatformId[] = useMemo(() => {
    const tp = ((topic as any).targetPlatforms ?? []) as PlatformId[];
    if (tp.length > 0) return tp;
    return activeAuthorPlatformIds;
  }, [topic, activeAuthorPlatformIds]);

  /** Phase 3.5.3 — platforms still available to add to this topic.
   *  Source: active author platform copies from the system catalog. */
  const addablePlatforms = useMemo(
    () => activeAuthorPlatformIds.filter((p) => !platforms.includes(p)),
    [activeAuthorPlatformIds, platforms],
  );

  const handleAddPlatform = async (platform: PlatformId) => {
    setAddPlatformAnchor(null);
    try {
      const next = [...platforms, platform];
      await new TopicBacklogFirestoreService().update(projectId, topic.id, {
        targetPlatforms: next,
      });
    } catch (e) {
      setBatchError(`Couldn't add platform: ${(e as Error).message}`);
    }
  };

  // ── Cell handlers ─────────────────────────────────────────────
  const handleGenerateMaster = (aspectKey: string | null) => {
    if (isSlotFocusMode) {
      setBatchError('Calendar slot generation is locked to the assigned platform cell.');
      return;
    }
    // Master generation reuses the per-cell dialog with target='master'.
    // We keep platform set to the first platform for prompt context but the
    // submission ignores it on master-only runs (Phase 3 will fully handle
    // master-only submission); for Phase 2 the user clicks Generate on a
    // derivative cell and master is created lazily by the cope chain.
    const platform = platforms[0];
    if (!platform) {
      setBatchError('Add an author/topic platform before generating a master post.');
      return;
    }
    setActiveDialog({
      aspectKey,
      platform,
      target: 'master',
    });
  };

  const handleGenerateDerivative = (
    aspectKey: string | null,
    platform: PlatformId,
  ) => {
    if (isSlotFocusMode && !isSlotCell(platform, aspectKey)) {
      setBatchError('This calendar slot can only generate its assigned platform/aspect cell.');
      return;
    }
    setActiveDialog({ aspectKey, platform, target: 'derivative' });
  };

  /**
   * Phase 3.5 — row-level batch action: deletes every stale derivative in
   * the row and fires a per-cell single-mode generation for each affected
   * platform. Variants/series are NOT preserved by this batch — users
   * who want multi-mode regen should reopen the per-cell dialog manually.
   *
   * Sequential per platform to keep load on the orchestrator predictable;
   * the orchestrator's master cache (Section 0.2) means subsequent platforms
   * reuse the master from the first call.
   */
  const handleRegenerateStaleRow = async (
    rowKey: string,
    aspectKey: string | null,
    modelId: string,
  ) => {
    if (isSlotFocusMode) {
      setBatchError('Regenerate stale is disabled in calendar slot mode.');
      return;
    }
    setStaleRegenBusy((prev) => new Set(prev).add(rowKey));
    setBatchError(null);
    try {
      const cellMap = grid.cells.get(rowKey);
      if (!cellMap) return;
      const stalePlatforms: PlatformId[] = [];
      for (const [platform, cell] of cellMap.entries()) {
        if (cell.staleCount > 0) {
          // Delete every stale item in the cell first.
          for (const item of cell.items) {
            if (item.staleSince) {
              await contentPostGridService.deletePost(projectId, item.id);
            }
          }
          stalePlatforms.push(platform);
        }
      }
      // Fire per-cell single-mode regen for each affected platform.
      const client = new ContentPlanApiClient();
      const applyMode = sprintId ? 'auto_apply_sprint' : 'auto_apply_topic';
      for (const platform of stalePlatforms) {
        await client.generatePosts({
          projectId, userId,
          authorId,
          scope: sprintId ? 'sprint' : 'topic',
          strategyId,
          sprintId,
          publicationId: matchingPublicationIdForCell(platform, aspectKey),
          topicIds: [topic.id],
          platforms: [platform as string],
          cellScope: {
            aspectKey,
            platform: platform as string,
            cellMode: 'single',
            method: 'cope',
          },
          applyMode,
          models: { platformPost: modelId },
        });
      }
    } catch (e) {
      setBatchError((e as Error).message);
    } finally {
      setStaleRegenBusy((prev) => {
        const next = new Set(prev);
        next.delete(rowKey);
        return next;
      });
      setStaleRegenDialog(null);
    }
  };

  /** Returns the count of stale derivative items across all platforms in a row. */
  const rowStaleCount = (rowKey: string): number => {
    const cellMap = grid.cells.get(rowKey);
    if (!cellMap) return 0;
    let n = 0;
    for (const cell of cellMap.values()) n += cell.staleCount;
    return n;
  };

  // ── Loading + error ───────────────────────────────────────────
  if (grid.error) {
    return (
      <Alert severity="error">
        Failed to load posts: {grid.error.message}
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5} data-testid="posts-grid">
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">Posts</Typography>
        {grid.loading && (
          <Typography variant="caption" color="text.secondary">
            Loading…
          </Typography>
        )}
      </Stack>

      {batchError && (
        <Alert severity="error" onClose={() => setBatchError(null)}>
          {batchError}
        </Alert>
      )}

      {/* Empty platforms guard. Renders when neither the topic nor the author
          has any platforms configured. */}
      {platforms.length === 0 && (
        <Alert severity="info">
          No platforms configured for this topic or its author. Add platforms
          to the author profile first.
        </Alert>
      )}

      {isSlotFocusMode && (
        <Alert severity="info" data-testid={CONTENT_PLAN.POSTS_GRID_SLOT_FOCUS_ALERT}>
          Calendar slot mode is locked to {publicationPlatform}
          {publicationAspectKey ? ` / ${publicationAspectKey}` : ' / main topic'}.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: platforms.length === 0
            ? `minmax(160px, 1.2fr) minmax(180px, 1fr) auto`
            : `minmax(160px, 1.2fr) minmax(180px, 1fr) repeat(${platforms.length}, minmax(180px, 1fr)) auto`,
          gap: 1,
          alignItems: 'stretch',
        }}
      >
        {/* Header row */}
        <Box sx={{ pb: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            ASPECT
          </Typography>
        </Box>
        <Box sx={{ pb: 0.5 }}>
          <Chip label="Master" size="small" color="primary" variant="outlined" />
        </Box>
        {platforms.map((p) => (
          <Box key={`hdr-${p}`} sx={{ pb: 0.5 }}>
            <Chip label={p} size="small" />
          </Box>
        ))}
        {/* [+ Platform] header action — last column. Disabled when no active
            author platforms remain to add. */}
        <Box sx={{ pb: 0.5, display: 'flex', alignItems: 'center' }}>
          <Tooltip title={isSlotFocusMode
            ? 'Locked to the calendar slot platform'
            : addablePlatforms.length === 0
              ? 'All platforms already added'
              : 'Add platform to this topic'}
          >
            <span>
              <IconButton
                size="small"
                disabled={isSlotFocusMode || addablePlatforms.length === 0}
                onClick={(e) => setAddPlatformAnchor(e.currentTarget)}
                data-testid="posts-grid-add-platform"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={addPlatformAnchor}
            open={Boolean(addPlatformAnchor)}
            onClose={() => setAddPlatformAnchor(null)}
          >
            {addablePlatforms.map((p) => (
              <MenuItem
                key={p}
                onClick={() => handleAddPlatform(p)}
                data-testid={`posts-grid-add-platform-${p}`}
              >
                {p}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Body rows */}
        {aspectRows.map((row) => {
          const masterModel = grid.mastersByAspect.get(row.key);
          const cellMap = grid.cells.get(row.key);
          return (
            <React.Fragment key={`row-${row.key}`}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  pr: 1,
                  gap: 0.5,
                }}
                data-testid={`posts-grid-row-label-${row.key}`}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: row.isOrphan ? 400 : 500,
                    fontStyle: row.isOrphan ? 'italic' : 'normal',
                    opacity: row.isOrphan ? 0.7 : 1,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {row.label}
                </Typography>
                {(() => {
                  if (isSlotFocusMode) return null;
                  const staleN = rowStaleCount(row.key);
                  if (staleN === 0) return null;
                  const isBusy = staleRegenBusy.has(row.key);
                  return (
                    <Chip
                      icon={isBusy ? <CircularProgress size={12} /> : <RefreshIcon fontSize="small" />}
                      label={isBusy
                        ? 'Regenerating…'
                        : `Regen stale (${staleN})`}
                      size="small"
                      color="warning"
                      variant="outlined"
                      onClick={isBusy ? undefined : () => {
                        setStaleRegenModel(projectDefaultModel.value);
                        setStaleRegenDialog({
                          rowKey: row.key,
                          aspectKey: row.scopeAspectKey,
                          count: staleN,
                        });
                      }}
                      data-testid={`posts-grid-row-regen-stale-${row.key}`}
                      sx={{ alignSelf: 'flex-start', cursor: isBusy ? 'wait' : 'pointer' }}
                    />
                  );
                })()}
              </Box>
              <MasterCell
                projectId={projectId}
                userId={userId}
                authorId={authorId}
                topicId={topic.id}
                aspectKey={row.scopeAspectKey}
                model={masterModel}
                busy={busyCell === `${row.key}::master`}
                pendingTaskId={pendingTasks.get(`${row.key}::master`) ?? null}
                strategyId={strategyId}
                sprintId={sprintId}
                regenPlatform={platforms[0] ?? null}
                regenPlatforms={platforms}
                onGenerate={() => handleGenerateMaster(row.scopeAspectKey)}
                onOpenPost={(p) => setOpenPost(p)}
                disableGenerate={row.isOrphan || isSlotFocusMode}
                disableGenerateReason={isSlotFocusMode
                  ? 'Calendar slot mode is locked to the assigned platform cell.'
                  : undefined}
              />
              {platforms.map((platform) => (
                <DerivativeCell
                  key={`cell-${row.key}-${platform}`}
                  projectId={projectId}
                  topicId={topic.id}
                  aspectKey={row.scopeAspectKey}
                  platform={platform}
                  model={cellMap?.get(platform)}
                  busy={busyCell === `${row.key}::${platform}`}
                  pendingTaskId={pendingTasks.get(`${row.key}::${platform}`) ?? null}
                  onGenerate={() => handleGenerateDerivative(row.scopeAspectKey, platform)}
                  onOpenPost={(p) => setOpenPost(p)}
                  disableGenerate={row.isOrphan || (isSlotFocusMode && !isSlotCell(platform, row.scopeAspectKey))}
                  disableGenerateReason={isSlotFocusMode && !isSlotCell(platform, row.scopeAspectKey)
                    ? 'Calendar slot mode is locked to the assigned platform/aspect cell.'
                    : undefined}
                />
              ))}
              {/* Trailing empty cell to align with the [+ Platform] header
                  (`auto` column). Keeps the grid aligned across header + body rows. */}
              <Box />
            </React.Fragment>
          );
        })}
      </Box>

      {activeDialog && (
        <PerCellGenerateDialog
          open
          onClose={() => setActiveDialog(null)}
          projectId={projectId}
          userId={userId}
          topic={topic}
          authorId={authorId}
          aspectKey={activeDialog.aspectKey}
          platform={activeDialog.platform}
          strategyId={strategyId}
          sprintId={sprintId}
          publicationId={activeDialog.target === 'derivative'
            ? matchingPublicationIdForCell(activeDialog.platform, activeDialog.aspectKey)
            : undefined}
          slotLocked={isSlotFocusMode && activeDialog.target === 'derivative'}
          slotAssignment={isSlotFocusMode && activeDialog.target === 'derivative'
            ? publicationSlotAssignment ?? undefined
            : undefined}
          target={activeDialog.target}
          rowPlatforms={activeDialog.target === 'master' ? platforms : undefined}
          onSubmitting={(busy, taskId) => {
            const rowKey = activeDialog.aspectKey ?? NO_ASPECT_KEY;
            const cellKey = activeDialog.target === 'master'
              ? `${rowKey}::master`
              : `${rowKey}::${activeDialog.platform}`;
            setBusyCell(busy ? cellKey : null);
            // When the API call resolves with a taskId (busy=false + taskId
            // present), record it on the cell so `<CellTaskBadge>` keeps
            // rendering until the backend task hits a terminal status.
            if (!busy && taskId) {
              setPendingTasks((prev) => {
                const next = new Map(prev);
                if (activeDialog.target === 'master') {
                  next.set(`${rowKey}::master`, taskId);
                  for (const platform of platforms) {
                    next.set(`${rowKey}::${platform}`, taskId);
                  }
                } else {
                  next.set(cellKey, taskId);
                }
                return next;
              });
            }
          }}
        />
      )}

      <SDialog
        open={Boolean(staleRegenDialog)}
        onClose={() => setStaleRegenDialog(null)}
        title="Regenerate stale posts"
        maxWidth="sm"
        fullWidth
        primaryAction={{
          label: staleRegenDialog && staleRegenBusy.has(staleRegenDialog.rowKey)
            ? 'Regenerating…'
            : 'Regenerate',
          onClick: () => {
            if (!staleRegenDialog) return;
            void handleRegenerateStaleRow(
              staleRegenDialog.rowKey,
              staleRegenDialog.aspectKey,
              staleRegenModel,
            );
          },
          loading: Boolean(staleRegenDialog && staleRegenBusy.has(staleRegenDialog.rowKey)),
          disabled: Boolean(staleRegenDialog && staleRegenBusy.has(staleRegenDialog.rowKey)),
          'data-testid': 'posts-grid-row-regen-stale-confirm',
        } as any}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setStaleRegenDialog(null),
          disabled: Boolean(staleRegenDialog && staleRegenBusy.has(staleRegenDialog.rowKey)),
        } as any}
      >
        <Stack spacing={2} sx={{ py: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Stale items in this row will be deleted before new posts are generated.
            {staleRegenDialog ? ` Affected posts: ${staleRegenDialog.count}.` : ''}
          </Typography>
          <AvailableModelSelector
            projectId={projectId}
            value={staleRegenModel}
            onChange={setStaleRegenModel}
            label="AI model"
            size="small"
          />
        </Stack>
      </SDialog>

      {/* Phase 3.6 — UnifiedPostDialog mount. One instance shared across
          all cells in the grid; opens on click of any post body (master or
          derivative). Save → updateContent on the canonical content_posts
          path; Delete → cell snapshot drops on next tick. */}
      {openPost && (() => {
        // v1.8.0 — Resolve the live Firestore-subscribed version of openPost
        // from `grid.allPosts` (refreshed by useTopicPostsGrid's onSnapshot).
        // Pass it as `live` prop so UnifiedPostDialog folds generation-result
        // fields (generatedUrl, status='completed', designAssetBrief updates)
        // into the editor state without overwriting user-typed edits.
        const liveDoc = (grid.allPosts ?? []).find((p) => p.id === openPost.id) ?? openPost;
        const livePostLike = contentPostToPostLike(liveDoc as any, {
          kind: 'content_post',
          projectId,
        });
        // scheduledAt lives on ContentPublication, not the canonical Post;
        // legacy content_posts docs may still carry it — read at the boundary.
        const liveScheduledAt = (liveDoc as Post & { scheduledAt?: string | null }).scheduledAt;
        return (
        <UnifiedPostDialog
          open
          onClose={() => setOpenPost(null)}
          post={contentPostToPostLike(openPost as any, {
            kind: 'content_post',
            projectId,
          })}
          live={livePostLike}
          publicationId={publicationId}
          publicationPlatform={publicationPlatform}
          publicationScheduledAt={liveScheduledAt ? new Date(liveScheduledAt).toISOString() : undefined}
          onSave={async (next: PostLike): Promise<string> => {
            const patch = postLikeToContentPostPatch(next) as Record<string, unknown>;
            // updateContent only allows specific narrowing keys. Strip
            // anything outside that whitelist. `title` / `notes` come from
            // the canonical Post (frontend types.ts) but ContentPost in
            // schema-models is narrower — we round-trip through `any` so
            // both surfaces stay in sync via the runtime shape.
            await contentPostFirestoreService.updateContent(
              projectId,
              openPost.id,
              {
                hook: patch.hook as string | undefined,
                body: patch.body as string | undefined,
                cta: patch.cta as string | undefined,
                title: patch.title as string | undefined,
                notes: patch.notes as string | undefined,
                aiAssets: patch.aiAssets as Record<string, any> | undefined,
              } as any,
            );
            return openPost.id;
          }}
          onDelete={async () => {
            await contentPostGridService.deletePost(projectId, openPost.id);
            setOpenPost(null);
          }}
          onGenerateImage={async (config) => {
            // Bridges AiAssetsEditor "Generate" button to ImageGenerationHandler.
            // Fire-and-forget: backend writes generatedUrl onto the slot's
            // experiment via its own subscriber, the AiAssetsEditor flips
            // status='completed' when Firestore emits the patch.
            try {
              const client = new ContentPlanApiClient();
              const resp = await client.generateImage({
                projectId, userId,
                ownerKind: 'content_post',
                ownerId: openPost.id,
                slotKey: config.slotKey,
                experimentId: config.experimentId ?? '',
                prompt: config.prompt,
                negativePrompt: config.negativePrompt,
                aspectRatio: config.aspectRatio,
                imageSize: config.imageSize,
                style: config.style,
                model: config.model,
                referenceImages: config.referenceImages,
              });
              if (!resp.success) return { success: false, error: resp.error };
              return { success: true };
            } catch (e) {
              return { success: false, error: (e as Error).message };
            }
          }}
          onRegenerateBrief={async () => {
            const client = new ContentPlanApiClient();
            await client.regenerateBrief({ projectId, userId, authorId, postId: openPost.id });
          }}
          onRegeneratePost={async () => {
            const client = new ContentPlanApiClient();
            await client.regeneratePost({ projectId, userId, authorId, postId: openPost.id });
          }}
        />
        );
      })()}
    </Stack>
  );
};

export default PostsGrid;
